/**
 * Fixture data for the live-agent mock transport (development only).
 *
 * Deliberately covers the shapes that are easy to get wrong: an anonymous
 * visitor with no contact details, a session already held by another agent, a
 * website-scoped session alongside property-scoped ones, all four log_kinds,
 * a UI_ACTION turn, and a long markdown answer.
 */

const minutesAgo = (n) => new Date(Date.now() - n * 60_000).toISOString();
const secondsAgo = (n) => new Date(Date.now() - n * 1000).toISOString();

export const MOCK_SESSIONS = [
  {
    session_id: "sess-aurora-1",
    conversation_id: 9001,
    property_id: 1,
    website_id: null,
    is_ai_paused: false,
    handling_agent_id: null,
    handling_agent_name: null,
    message_count: 6,
    contact_name: "Priya Raman",
    contact_email: "priya.raman@example.com",
    contact_phone: "+1 415 555 0132",
    intent: "tour",
    last_activity_at: secondsAgo(12),
    last_seen: secondsAgo(4),
  },
  {
    session_id: "sess-aurora-2",
    conversation_id: 9002,
    property_id: 1,
    website_id: null,
    is_ai_paused: false,
    handling_agent_id: null,
    handling_agent_name: null,
    message_count: 2,
    contact_name: null,
    contact_email: null,
    contact_phone: null,
    intent: "pricing",
    last_activity_at: secondsAgo(48),
    last_seen: secondsAgo(20),
  },
  {
    session_id: "sess-aurora-3",
    conversation_id: 9003,
    property_id: 1,
    website_id: null,
    is_ai_paused: true,
    handling_agent_id: 77,
    handling_agent_name: "Dana Whitfield",
    message_count: 11,
    contact_name: "Marcus Bell",
    contact_email: "mbell@example.com",
    contact_phone: null,
    intent: "apply",
    last_activity_at: minutesAgo(1),
    last_seen: secondsAgo(30),
  },
  {
    session_id: "sess-harbor-1",
    conversation_id: 9004,
    property_id: 2,
    website_id: null,
    is_ai_paused: false,
    handling_agent_id: null,
    handling_agent_name: null,
    message_count: 4,
    contact_name: "Lin Zhao",
    contact_email: "lin.zhao@example.com",
    contact_phone: null,
    intent: "availability",
    last_activity_at: minutesAgo(2),
    last_seen: minutesAgo(1),
  },
  {
    session_id: "sess-web-1",
    conversation_id: 9005,
    property_id: null,
    website_id: 10,
    is_ai_paused: false,
    handling_agent_id: null,
    handling_agent_name: null,
    message_count: 3,
    contact_name: "Sam Okafor",
    contact_email: null,
    contact_phone: "+1 202 555 0188",
    intent: "maintenance",
    last_activity_at: minutesAgo(3),
    last_seen: minutesAgo(2),
  },
  {
    session_id: "sess-web-2",
    conversation_id: 9006,
    property_id: null,
    website_id: 10,
    is_ai_paused: false,
    handling_agent_id: null,
    handling_agent_name: null,
    message_count: 1,
    contact_name: null,
    contact_email: null,
    contact_phone: null,
    intent: "other",
    last_activity_at: minutesAgo(5),
    last_seen: minutesAgo(4),
  },
];

export const MOCK_TRANSCRIPTS = {
  "sess-aurora-1": [
    {
      turn_id: "t-1-a",
      created_at: minutesAgo(6),
      log_kind: "chat",
      question: "Do you have any 2 bedroom units available in March?",
      response:
        "Yes — we have **two** 2-bedroom homes opening up in March at Aurora Heights:\n\n" +
        "- **Unit 412** — $2,450/mo, available Mar 3\n" +
        "- **Unit 707** — $2,610/mo, available Mar 18\n\n" +
        "Both include in-unit laundry and a private balcony. Would you like to schedule a tour?",
    },
    {
      turn_id: "t-1-b",
      created_at: minutesAgo(5),
      log_kind: "chat",
      question: "Is parking included?",
      response:
        "Covered parking is $150/mo per space, and the first space is waived on a 14-month lease.",
    },
    {
      turn_id: "t-1-c",
      created_at: minutesAgo(4),
      log_kind: "chat",
      question: "",
      response: "UI_ACTION: tour",
    },
    {
      turn_id: "t-1-d",
      created_at: minutesAgo(2),
      log_kind: "chat",
      question: "Can I see 412 this Saturday morning?",
      response:
        "Saturday morning works — I can offer 10:00 or 11:30. Which suits you better?",
    },
  ],

  "sess-aurora-3": [
    {
      turn_id: "t-3-a",
      created_at: minutesAgo(14),
      log_kind: "chat",
      question: "I started an application yesterday but the page errored out.",
      response:
        "I'm sorry about that. Let me check whether your application was saved.",
    },
    {
      turn_id: "t-3-b",
      created_at: minutesAgo(9),
      log_kind: "system",
      question: "",
      response: "Dana Whitfield joined the conversation.",
    },
    {
      turn_id: "t-3-c",
      created_at: minutesAgo(8),
      log_kind: "agent",
      question: "",
      response:
        "Hi Marcus, Dana here from the leasing team. I can see a draft application under your email — nothing was lost.",
      agent_name: "Dana Whitfield",
      is_bot: false,
    },
    {
      turn_id: "t-3-d",
      created_at: minutesAgo(7),
      log_kind: "cust",
      question: "Oh good. Can you just resend me the link?",
      response: "",
    },
    {
      turn_id: "t-3-e",
      created_at: minutesAgo(6),
      log_kind: "agent",
      question: "",
      response:
        "Sent it to mbell@example.com just now — it should land in a minute or two.",
      agent_name: "Dana Whitfield",
      is_bot: false,
    },
  ],

  "sess-aurora-2": [
    {
      turn_id: "t-2-a",
      created_at: minutesAgo(1),
      log_kind: "chat",
      question: "how much is a studio",
      response:
        "Studios at Aurora Heights start at $1,795/mo. Would you like to see what's available?",
    },
  ],

  "sess-harbor-1": [
    {
      turn_id: "t-4-a",
      created_at: minutesAgo(4),
      log_kind: "chat",
      question: "Whats the earliest move in date for a 1 bed?",
      response:
        "The earliest 1-bedroom move-in at Harbor Point is February 12.",
    },
  ],

  "sess-web-1": [
    {
      turn_id: "t-5-a",
      created_at: minutesAgo(5),
      log_kind: "chat",
      question: "my dishwasher is leaking, who do I contact",
      response:
        "I can log that for you. Maintenance requests are handled through the resident portal, but I can flag it as urgent — is water still actively leaking?",
    },
  ],
};

/** Rotating messages the mock sends as inbound customer traffic. */
export const MOCK_INBOUND = [
  "Sorry, one more question —",
  "Is the deposit refundable?",
  "Do you allow dogs over 40lbs?",
  "What utilities are included in rent?",
  "Could someone call me instead?",
  "Is there a waitlist for the corner units?",
];
